import _ from "lodash";
import { default as LoadersBase } from "./base.js";
import { default as ModelsFrame } from "models/metadata/frame.js";
import { default as ModelsStack } from "models/metadata/stack.js";
import { default as ModelsSeries } from "models/metadata/series.js";
import { default as ParserDicom } from "../parser/dicom.js";

// temp
import config from "config";
import Helper from "helper/index";
function getLUT(pixelRepresentation, lutDataSet) {
  let numLUTEntries = lutDataSet.uint16("x00283002", 0);

  if (numLUTEntries === 0) {
    numLUTEntries = 65535;
  }
  let firstValueMapped = 0;

  if (pixelRepresentation === 0) {
    firstValueMapped = lutDataSet.uint16("x00283002", 1);
  } else {
    firstValueMapped = lutDataSet.int16("x00283002", 1);
  }
  const numBitsPerEntry = lutDataSet.uint16("x00283002", 2);
  const lut = {
    id: "1",
    firstValueMapped,
    numBitsPerEntry,
    lut: [],
  };

  for (let i = 0; i < numLUTEntries; i++) {
    if (pixelRepresentation === 0) {
      lut.lut[i] = lutDataSet.uint16("x00283006", i);
    } else {
      lut.lut[i] = lutDataSet.int16("x00283006", i);
    }
  }

  return lut;
}

function getLUTs(pixelRepresentation, lutSequence) {
  if (!lutSequence || !lutSequence.items.length) {
    return;
  }
  const luts = [];

  for (let i = 0; i < lutSequence.items.length; i++) {
    const lutDataSet = lutSequence.items[i].dataSet;
    const lut = getLUT(pixelRepresentation, lutDataSet);

    if (lut) {
      luts.push(lut);
    }
  }

  return luts;
}

function getModalityLUTOutputPixelRepresentation(
  sopClassUID,
  rescaleIntercept,
  rescaleSlope,
  minStoredPixelValue,
  modalityLUTSequence,
  pixelRepresentation
) {
  // CT SOP Classes are always signed
  if (
    sopClassUID === "1.2.840.10008.5.1.4.1.1.2" ||
    sopClassUID === "1.2.840.10008.5.1.4.1.1.2.1"
  ) {
    return 1;
  }

  // if rescale intercept and rescale slope are present, pass the minimum stored
  // pixel value through them to see if we get a signed output range
  if (rescaleIntercept !== undefined && rescaleSlope !== undefined) {
    const minModalityLutValue =
      minStoredPixelValue * rescaleSlope + rescaleIntercept;

    if (minModalityLutValue < 0) {
      return 1;
    }

    return 0;
  }

  // Output of non linear modality lut is always unsigned
  if (modalityLUTSequence && modalityLUTSequence.length > 0) {
    return 0;
  }

  // If no modality lut transform, output is same as pixel representation
  return pixelRepresentation;
}

function isColorImage(photoMetricInterpretation) {
  return (
    photoMetricInterpretation === "RGB" ||
    photoMetricInterpretation === "PALETTE COLOR" ||
    photoMetricInterpretation === "YBR_FULL" ||
    photoMetricInterpretation === "YBR_FULL_422" ||
    photoMetricInterpretation === "YBR_PARTIAL_422" ||
    photoMetricInterpretation === "YBR_PARTIAL_420" ||
    photoMetricInterpretation === "YBR_RCT" ||
    photoMetricInterpretation === "YBR_ICT"
  );
}

function isModalityLUTForDisplay(sopClassUid) {
  // special case for XA and XRF
  // https://groups.google.com/forum/#!searchin/comp.protocols.dicom/Modality$20LUT$20XA/comp.protocols.dicom/UBxhOZ2anJ0/D0R_QP8V2wIJ
  return (
    sopClassUid !== "1.2.840.10008.5.1.4.1.1.12.1" && // XA
    sopClassUid !== "1.2.840.10008.5.1.4.1.1.12.2.1"
  ); // XRF
}

function parseUrl(url, base = "") {
  const parsedUrl = new URL(url, base);

  const data = {
    filename: parsedUrl.searchParams.get("filename"),
    extension: "",
    pathname: parsedUrl.pathname,
    query: parsedUrl.search,
  };

  // get file name
  if (!data.filename) {
    data.filename = data.pathname.split("/").pop();
  }

  // find extension
  const splittedName = data.filename.split(".");
  data.extension = splittedName.length > 1 ? splittedName.pop() : "dicom";

  const skipExt = [
    "asp",
    "aspx",
    "go",
    "gs",
    "hs",
    "jsp",
    "js",
    "php",
    "pl",
    "py",
    "rb",
    "htm",
    "html",
  ];

  if (
    !isNaN(data.extension) ||
    skipExt.indexOf(data.extension) !== -1 ||
    (data.query && data.query.includes("contentType=application%2Fdicom"))
  ) {
    data.extension = "dicom";
  }

  return data;
}

export default class LoaderDicom extends LoadersBase {
  /**
   *
   * @param {*} response
   */

  parseFile(dataname, readFiles) {
    this.emit("parse-start", {
      file: dataname,
      time: new Date(),
    });
    // give a chance to the UI to update because
    // after the rendering will be blocked with intensive JS
    // will be removed after eventer set up
    if (this._progressBar) {
      this._progressBar.update(0, 100, "parse", dataname);
    }

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(
          new Promise((resolve, reject) => {
            let data = readFiles;

            if (!Array.isArray(readFiles)) {
              data = [readFiles];
            }

            data.forEach((dataset) => {
              this._preprocessFile(dataname, dataset);
            });

            if (data.length === 1) {
              data = data[0];
            } else {
              // if raw/mhd pair
              let mhdFile = data.filter(
                this._filterByExtension.bind(null, "MHD")
              );
              let rawFile = data.filter(
                this._filterByExtension.bind(null, "RAW")
              );
              if (
                data.length === 2 &&
                mhdFile.length === 1 &&
                rawFile.length === 1
              ) {
                data.url = mhdFile[0].url;
                data.extension = mhdFile[0].extension;
                data.mhdBuffer = mhdFile[0].buffer;
                data.rawBuffer = rawFile[0].buffer;
              }
            }

            let Parser = this._parser(data.extension);
            if (!Parser) {
              // emit 'parse-error' event
              this.emit("parse-error", {
                file: dataname,
                time: new Date(),
                error: data.filename + "can not be parsed.",
              });
              reject(data.filename + " can not be parsed.");
            }

            // check extension
            let volumeParser = null;
            try {
              volumeParser = new Parser(data, 0);
              //volumeParser = new Parser(new Uint8Array(data), 0);
              //const dicomLoader_NS = new NSDicomLoaderAdapter(data);
              //volumeParser = new NSDicomLoaderAdapter(data);
              var a = 10;
            } catch (e) {
              console.warn(e);
              // emit 'parse-error' event
              this.emit("parse-error", {
                file: dataname,
                time: new Date(),
                error: e,
              });
              reject(e);
            }

            // create a series
            let series = new ModelsSeries();
            // global information
            series.seriesInstanceUID = volumeParser.seriesInstanceUID();
            series.transferSyntaxUID = volumeParser.transferSyntaxUID();
            series.seriesDate = volumeParser.seriesDate();
            series.seriesDescription = volumeParser.seriesDescription();
            series.seriesNumber = volumeParser.seriesNumber();

            series.studyInstanceUID = volumeParser.studyInstanceUID();
            series.studyID = volumeParser.studyID();
            series.studyDate = volumeParser.studyDate();
            series.studyTime = volumeParser.studyTime();
            series.studyDescription = volumeParser.studyDescription();
            series.numberOfFrames = volumeParser.numberOfFrames();
            if (!series.numberOfFrames) {
              series.numberOfFrames = 1;
            }
            series.numberOfChannels = volumeParser.numberOfChannels();
            series.modality = volumeParser.modality();
            // if it is a segmentation, attach extra information
            if (series.modality === "SEG") {
              // colors
              // labels
              // etc.
              series.segmentationType = volumeParser.segmentationType();
              series.segmentationSegments = volumeParser.segmentationSegments();
            }
            // patient information
            series.patientID = volumeParser.patientID();
            series.patientName = volumeParser.patientName();
            series.patientAge = volumeParser.patientAge();
            series.patientBirthdate = volumeParser.patientBirthdate();
            series.patientSex = volumeParser.patientSex();

            // Institution information
            series.institutionName = volumeParser.institutionName();
            series.institutionAddress = volumeParser.institutionAddress();

            // Machine information
            series.XRayTubeCurrent = volumeParser.XRayTubeCurrent();
            series.KVP = volumeParser.KVP();
            series.acquisitionDate = volumeParser.acquisitionDate();
            series.acquisitionTime = volumeParser.acquisitionTime();

            series.sliceThickness = volumeParser.sliceThickness();
            series.sliceLocation = volumeParser.sliceLocation();

            // Window Center/Width information
            series.windowCenter = volumeParser.windowCenter();
            series.windowWidth = volumeParser.windowWidth();

            // Image comment
            series.imageComment = volumeParser.imageComment();
            series.imageComment = volumeParser.studyComment();

            // information for MR modality
            series.fieldStrenth = volumeParser.fieldStrenth();
            series.repetitionTime = volumeParser.repetitionTime();
            series.echoTime = volumeParser.echoTime();

            // Orientation information
            // get row/colume Cosine
            let imageOrientationPatient = volumeParser.imageOrientation();
            let rowCosines = null;
            let columnCosines = null;
            if (imageOrientationPatient) {
              rowCosines = [
                parseFloat(imageOrientationPatient[0]),
                parseFloat(imageOrientationPatient[1]),
                parseFloat(imageOrientationPatient[2]),
              ];
              columnCosines = [
                parseFloat(imageOrientationPatient[3]),
                parseFloat(imageOrientationPatient[4]),
                parseFloat(imageOrientationPatient[5]),
              ];
            }

            series.rowCosines = rowCosines;
            series.columnCosines = columnCosines;
            series.imageOrientationPatient = imageOrientationPatient;

            // just create 1 dummy stack for now
            let stack = new ModelsStack();
            stack.numberOfChannels = volumeParser.numberOfChannels();
            stack.pixelRepresentation = volumeParser.pixelRepresentation();
            stack.pixelType = volumeParser.pixelType();
            stack.invert = volumeParser.invert();
            stack.spacingBetweenSlices = volumeParser.spacingBetweenSlices();
            stack.modality = series.modality;
            // if it is a segmentation, attach extra information
            if (stack.modality === "SEG") {
              // colors
              // labels
              // etc.
              stack.segmentationType = series.segmentationType;
              stack.segmentationSegments = series.segmentationSegments;
            }
            series.stack.push(stack);
            // recursive call for each frame
            // better than for loop to be able
            // to update dom with "progress" callback
            setTimeout(
              this.parseFrameClosure(
                series,
                stack,
                dataname,
                0,
                volumeParser,
                resolve,
                reject
              ),
              0
            );
          })
        );
      }, 10);
    });
  }

  /**
   * Parse response. (WADO)
   * response 포맷은 아래와 같다고 가정. :
   *    {
   *      url: 'resource url',
   *      buffer: xmlresponse,
   *    }
   * @param {object} response - response
   * @return {promise} promise
   */
  parse(response) {
    // emit 'parse-start' event
    this.emit("parse-start", {
      file: response.url,
      time: new Date(),
    });

    // give a chance to the UI to update because
    // after the rendering will be blocked with intensive JS
    // will be removed after eventer set up
    if (this._progressBar) {
      this._progressBar.update(0, 100, "parse", response.url);
    }

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(
          new Promise((resolve, reject) => {
            let data = response;

            if (!Array.isArray(data)) {
              data = [data];
            }

            data.forEach((dataset) => {
              this._preprocess(dataset);
            });

            if (data.length === 1) {
              data = data[0];
            } else {
              // if raw/mhd pair
              let mhdFile = data.filter(
                this._filterByExtension.bind(null, "MHD")
              );
              let rawFile = data.filter(
                this._filterByExtension.bind(null, "RAW")
              );
              if (
                data.length === 2 &&
                mhdFile.length === 1 &&
                rawFile.length === 1
              ) {
                data.url = mhdFile[0].url;
                data.extension = mhdFile[0].extension;
                data.mhdBuffer = mhdFile[0].buffer;
                data.rawBuffer = rawFile[0].buffer;
              }
            }

            let Parser = this._parser(data.extension);
            if (!Parser) {
              // emit 'parse-error' event
              this.emit("parse-error", {
                file: response.url,
                time: new Date(),
                error: data.filename + "can not be parsed.",
              });
              reject(data.filename + " can not be parsed.");
            }

            // check extension
            let volumeParser = null;
            try {
              volumeParser = new Parser(data, 0);
            } catch (e) {
              console.warn(e);
              // emit 'parse-error' event
              this.emit("parse-error", {
                file: response.url,
                time: new Date(),
                error: e,
              });
              reject(e);
            }

            // create a series
            let series = new ModelsSeries();
            // global information
            series.seriesInstanceUID = volumeParser.seriesInstanceUID();
            series.transferSyntaxUID = volumeParser.transferSyntaxUID();
            series.seriesDate = volumeParser.seriesDate();
            series.seriesDescription = volumeParser.seriesDescription();
            series.studyDate = volumeParser.studyDate();
            series.studyTime = volumeParser.studyTime();
            series.studyInstanceID = volumeParser.studyInstanceUID();
            series.studyDescription = volumeParser.studyDescription();
            series.numberOfFrames = volumeParser.numberOfFrames();
            if (!series.numberOfFrames) {
              series.numberOfFrames = 1;
            }
            series.numberOfChannels = volumeParser.numberOfChannels();
            series.modality = volumeParser.modality();
            // if it is a segmentation, attach extra information
            if (series.modality === "SEG") {
              // colors
              // labels
              // etc.
              series.segmentationType = volumeParser.segmentationType();
              series.segmentationSegments = volumeParser.segmentationSegments();
            }
            // patient information
            series.patientID = volumeParser.patientID();
            series.patientName = volumeParser.patientName();
            series.patientAge = volumeParser.patientAge();
            series.patientBirthdate = volumeParser.patientBirthdate();
            series.patientSex = volumeParser.patientSex();

            // just create 1 dummy stack for now
            let stack = new ModelsStack();
            stack.numberOfChannels = volumeParser.numberOfChannels();
            stack.pixelRepresentation = volumeParser.pixelRepresentation();
            stack.pixelType = volumeParser.pixelType();
            stack.invert = volumeParser.invert();
            stack.spacingBetweenSlices = volumeParser.spacingBetweenSlices();
            stack.modality = series.modality;
            // if it is a segmentation, attach extra information
            if (stack.modality === "SEG") {
              // colors
              // labels
              // etc.
              stack.segmentationType = series.segmentationType;
              stack.segmentationSegments = series.segmentationSegments;
            }
            series.stack.push(stack);
            // recursive call for each frame
            // better than for loop to be able
            // to update dom with "progress" callback
            setTimeout(
              this.parseFrameClosure(
                series,
                stack,
                response.url,
                0,
                volumeParser,
                resolve,
                reject
              ),
              0
            );
          })
        );
      }, 10);
    });
  }

  parseFrameClosure(series, stack, url, i, dataParser, resolve, reject) {
    return () => {
      this.parseFrame(series, stack, url, i, dataParser, resolve, reject);
    };
  }

  /**
   * recursive parse frame
   * @param {ModelsSeries} series - data series
   * @param {ModelsStack} stack - data stack
   * @param {string} url - resource url
   * @param {number} i - frame index
   * @param {parser} dataParser - selected parser
   * @param {promise.resolve} resolve - promise resolve args
   * @param {promise.reject} reject - promise reject args
   */
  parseFrame(series, stack, url, i, dataParser, resolve, reject) {
    let frame = new ModelsFrame();
    frame.sopInstanceUID = dataParser.sopInstanceUID(i);
    frame.sopClassUID = dataParser.sopClassUID(i);
    frame.url = url;
    frame.index = i;
    frame.invert = stack.invert;
    frame.frameTime = dataParser.frameTime(i);
    frame.ultrasoundRegions = dataParser.ultrasoundRegions(i);
    frame.rows = dataParser.rows(i);
    frame.height = frame.rows;
    frame.columns = dataParser.columns(i);
    frame.width = frame.columns;
    frame.numberOfChannels = stack.numberOfChannels;
    frame.pixelPaddingValue = dataParser.pixelPaddingValue(i);
    frame.pixelRepresentation = stack.pixelRepresentation;
    frame.patientWeight = dataParser.patientWeight();

    frame.seriesTime = dataParser.seriesTime();
    frame.radiopharmaceuticalInfo = dataParser.radiopharmaceuticalInfo();
    /**
     * @note Pixel representation 의 값 범위는 0~1 이며, 값이 유효하지 않은 경우
     * 0으로 배정한다. 이 후, Modality 및 Pixel 값을 이용해 적절한 Pixel representation
     * 값을 확인 한 후, 필요시 픽셀 값을 다시 디코딩
     */
    if (
      frame.pixelRepresentation < 0 ||
      frame.pixelRepresentation > 1 ||
      Helper.Validator.isDefined(frame.pixelRepresentation) === false ||
      Helper.Validator.isNumber(frame.pixelRepresentation) === false
    ) {
      frame.pixelRepresentation = 0;
    }
    frame.pixelType = stack.pixelType;
    frame.pixelData = dataParser.extractPixelData(i);

    frame.hasPhysicalDelta = true;
    if (
      _.get(frame.ultrasoundRegions, "[0].deltaX", null) === null ||
      _.get(frame.ultrasoundRegions, "[0].deltaY", null) === null
    ) {
      frame.hasPhysicalDelta = false;
    }

    // 2019.11.19 NumberOfFrame 과 실제 저장된 프레임 수가 다른경우 예외처리
    if (frame.pixelData === null) {
      resolve(series);
      return;
    }

    frame.pixelSpacing = dataParser.pixelSpacing(i);
    //frame.isExistPixelSpacing = frame.pixelSpacing === null ? false : true;
    frame.isExistPixelSpacing = true;
    if (
      frame.pixelSpacing === null ||
      frame.pixelSpacing === undefined ||
      (frame.pixelSpacing[0] === 0 && frame.pixelSpacing[1] === 0)
    ) {
      frame.isExistPixelSpacing = false;
    }
    if (frame.isExistPixelSpacing === true) {
      frame.rowPixelSpacing = frame.pixelSpacing[0];
      frame.columnPixelSpacing = frame.pixelSpacing[1];
    }
    frame.usePixelSpacingApproximateValue = false;
    frame.isEqualRowColumn = frame.rows === frame.columns ? true : false;

    /// reconstructiondiameter 태그 존재 확인
    frame.isExistReconstructionDiameter = true;
    if (
      dataParser.reconstructionDiameter() === null ||
      dataParser.reconstructionDiameter() === undefined ||
      isNaN(dataParser.reconstructionDiameter())
    ) {
      frame.isExistReconstructionDiameter = false;
    }
    frame.isExistRowColumn = false;
    if (
      frame.rows !== null &&
      frame.rows !== undefined &&
      frame.columns !== null &&
      frame.columns !== undefined
    ) {
      frame.isExistRowColumn = true;
    }

    dataParser.reconstructionDiameter();
    // pixelSpacing 값이 부재인 경우, 프로그램 설정에 따라 근사치 사용
    if (
      frame.isExistPixelSpacing === false &&
      frame.isExistRowColumn === true &&
      frame.isExistReconstructionDiameter === true &&
      frame.isEqualRowColumn &&
      config.dicomTag.usePixelSpacingApproximateValue === true
    ) {
      frame.usePixelSpacingApproximateValue = true;

      if (
        Helper.Validator.isNumber(
          dataParser.reconstructionDiameter() / frame.rows
        ) === true
      ) {
        frame.pixelSpacing = [
          dataParser.reconstructionDiameter() / frame.rows,
          dataParser.reconstructionDiameter() / frame.rows,
        ];
      } else {
        frame.pixelSpacing = null;
        frame.usePixelSpacingApproximateValue = false;
      }
    }

    /**
     * @note Pixel Spacing이 없는 경우, 영상에 대한 기본 동작을 위해 기본 값 [1,1]를 할당하고
     * isExistPixelSpacing 을 false 로 배정하여 Pixel Spacing 부재에 따른 데이터에 대한 예외처리를 수행한다.
     */
    if (
      _.get(frame, "pixelSpacing", null) === null ||
      frame.pixelSpacing[0] === 0 ||
      frame.pixelSpacing[1] === 0
    ) {
      frame.pixelSpacing = [1, 1];
      frame.isExistPixelSpacing = false;
    }

    frame.performedProcedureStepID = dataParser.performedProcedureStepID();
    frame.spacingBetweenSlices = dataParser.spacingBetweenSlices(i);
    frame.sliceThickness = dataParser.sliceThickness(i);
    frame.sliceLocation = dataParser.sliceLocation(i);
    frame.imageOrientation = dataParser.imageOrientation(i);
    frame.rightHanded = dataParser.rightHanded();
    frame.acquisitionDate = dataParser.acquisitionDate();
    frame.acquisitionTime = dataParser.acquisitionTime();

    stack.rightHanded = frame.rightHanded;
    if (frame.imageOrientation === null) {
      frame.imageOrientation = [1, 0, 0, 0, 1, 0];
    }
    frame.imagePosition = dataParser.imagePosition(i);
    /*
        null ImagePosition should not be handle here
        if (frame.imagePosition === null) {
          frame.imagePosition = [0, 0, i];
        }*/
    frame.dimensionIndexValues = dataParser.dimensionIndexValues(i);
    frame.bitsAllocated = dataParser.bitsAllocated(i);
    frame.instanceNumber = dataParser.instanceNumber(i);
    frame.windowCenter = dataParser.windowCenter(i);
    frame.windowWidth = dataParser.windowWidth(i);
    frame.rescaleSlope = dataParser.rescaleSlope(i);
    frame.rescaleIntercept = dataParser.rescaleIntercept(i);

    // should pass frame index for consistency...

    frame.minMax = dataParser.minMaxPixelData(frame.pixelData);

    frame.photometricInterpretation = dataParser.photometricInterpretation();
    frame.VOILutSequence = dataParser.getVOILutSequence();
    frame.modalityLutSequence = dataParser.getModalityLUTSequence();
    // 아래는 위에서 파싱된 정보를 이용해 몇가지 2차 정보를 생성함.
    frame.color = isColorImage(frame.photometricInterpretation); // is color image?

    frame.frameOfReferenceUID = dataParser.frameOfReferenceUID();
    frame.studyDescription = dataParser.studyDescription();
    frame.seriesInstanceUID = dataParser.seriesInstanceUID();
    frame.patientName = dataParser.patientName();
    frame.modality = dataParser.modality();
    // 데이터 덤프 획득
    frame.dump = dataParser.dumpDataSet();
    // if series.mo
    if (series.modality === "SEG") {
      frame.referencedSegmentNumber = dataParser.referencedSegmentNumber(i);
    }

    // Modality LUT
    if (
      frame.pixelRepresentation &&
      frame.pixelRepresentation.length > 0 &&
      isModalityLUTForDisplay(frame.sopClassUID)
    ) {
      frame.modalityLUT = frame.pixelRepresentation[0];
    }

    //if (frame.isColorImage) {
    if (frame.color) {
      frame.windowWidth = 255;
      frame.windowCenter = 127;
    }

    // PET Unit 정보 획득
    // PET Unit 정보를 기반으로 SUV 계산 이 가능한 단위인지 확인
    frame.unitForPET = dataParser.unitForPET() || "";
    const UnitsForSUVSupport = ["BQML".toLowerCase(), "GML".toLowerCase()];
    if (UnitsForSUVSupport.includes(frame.unitForPET.toLowerCase()) === true) {
      frame.isSupportSUV = true;
    } else {
      frame.isSupportSUV = false;
    }

    // if windowCenter or windowWidth is not defined, set value.
    // set the ww/wc to cover the dynamic range of the image if no values are supplied
    if (
      frame.windowCenter === undefined ||
      frame.windowCenter === null ||
      frame.windowWidth === undefined ||
      frame.windowWidth === null
    ) {
      const maxVoi =
        Math.max(frame.minMax[0], frame.minMax[1]) * frame.rescaleSlope +
        frame.rescaleIntercept;
      const minVoi =
        Math.min(frame.minMax[0], frame.minMax[1]) * frame.rescaleSlope +
        frame.rescaleIntercept;

      // Note : when missing window width/center tag,
      // default window width/center using rescale min/max.
      // frame.windowWidth = maxVoi - minVoi;
      // frame.windowCenter = (maxVoi + minVoi) / 2;

      // like radiant view window width/center using rescale min/max.
      let correctionValueWC = 0;
      if (maxVoi + minVoi > 0) {
        correctionValueWC = 1;
      }
      frame.windowCenter = parseInt((maxVoi + minVoi) / 2) + correctionValueWC;
      frame.windowWidth = maxVoi - minVoi + 1;
    }

    const modalityLUTOutputPixelRepresentation = getModalityLUTOutputPixelRepresentation(
      frame.sopClassUid,
      frame.rescaleIntercept,
      frame.rescaleSlope,
      Math.min(frame.minMax[0], frame.minMax[1]),
      frame.modalityLutSequence,
      frame.pixelRepresentation
    );

    // modality가 US이고, Pixel representation이 undefined 이며, ModalityLUTOutputPixelRepresentation이 0이 아닌경우
    // 픽셀 디코딩을 재수행
    if (
      frame.modality.toUpperCase() === "US" &&
      frame.pixelRepresentation === undefined &&
      modalityLUTOutputPixelRepresentation === 0
    ) {
      frame.pixelData = dataParser.extractPixelData(
        i,
        frame.pixelRepresentation
      );
    }

    frame.voiLUTSequence = getLUTs(
      modalityLUTOutputPixelRepresentation,
      frame.getVOILutSequence
    );

    // row/column cosines
    if (frame.imageOrientation) {
      frame.rowCosines = [
        parseFloat(frame.imageOrientation[0]),
        parseFloat(frame.imageOrientation[1]),
        parseFloat(frame.imageOrientation[2]),
      ];
      frame.columnCosines = [
        parseFloat(frame.imageOrientation[3]),
        parseFloat(frame.imageOrientation[4]),
        parseFloat(frame.imageOrientation[5]),
      ];
    }

    stack.frame.push(frame);

    // update status
    this._parsed = i + 1;
    this._totalParsed = series.numberOfFrames;

    // will be removed after eventer set up
    if (this._progressBar) {
      this._progressBar.update(this._parsed, this._totalParsed, "parse", url);
    }

    // emit 'parsing' event
    this.emit("parsing", {
      file: url,
      total: this._totalParsed,
      parsed: this._parsed,
      time: new Date(),
    });

    if (this._parsed === this._totalParsed) {
      // emit 'parse-success' event
      this.emit("parse-success", {
        file: url,
        total: this._totalParsed,
        parsed: this._parsed,
        time: new Date(),
      });
      series.numOfParsing = this._parsed;
      resolve(series);
    } else {
      setTimeout(
        this.parseFrameClosure(
          series,
          stack,
          url,
          this._parsed,
          dataParser,
          resolve,
          reject
        ),
        0
      );
    }
  }

  _parser(extension) {
    let Parser = null;

    switch (extension.toUpperCase()) {
      case "DICOM":
      case "DCM":
        Parser = ParserDicom;
        break;
      default:
        console.warn("warn : unspoorted extension : " + extension);
    }

    return Parser;
  }

  /**
   *
   */
  _preprocess(data) {
    // update data
    const parsedUrl = parseUrl(data.url);
    data.filename = parsedUrl.filename;
    data.extension = parsedUrl.extension;
    data.pathname = parsedUrl.pathname;
    data.query = parsedUrl.query;
    data.gzcompressed = false;
  }
  _preprocessFile(filename, data) {
    // update data
    data.filename = filename;
    data.extension = "DICOM";
    data.gzcompressed = false;
  }

  /**
   * Filter data by extension
   * @param {*} extension
   * @param {*} item
   * @returns Boolean
   */
  _filterByExtension(extension, item) {
    if (item.extension.toUpperCase() === extension.toUpperCase()) {
      return true;
    }
    return false;
  }
}
